import React, { useState } from 'react';
import { FaTrashAlt, FaEdit, FaSave, FaTimes } from 'react-icons/fa';

export default function TablaCostosSL({ productos, editarProducto, eliminarProducto, margenObjetivo = 25 }) {
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({ costoBase: '', precioSugerido: '' });

    const formatCurrency = (value) => Number(value).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

    const handleEditClick = (item) => {
        setEditingId(item.id);
        // Si es un producto viejo de scarjav, leemos materiales, si no, costoBase
        const costo = parseFloat(item.costoBase) || parseFloat(item.materiales) || 0;
        const precio = parseFloat(item.precioSugerido) || parseFloat(item.costo) || 0;
        setEditForm({ costoBase: costo, precioSugerido: precio });
    };

    const handleSave = async (id) => {
        await editarProducto(id, {
            costoBase: parseFloat(editForm.costoBase),
            precioSugerido: parseFloat(editForm.precioSugerido)
        });
        setEditingId(null);
    };

    return (
        <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm text-left text-gray-600">
                <thead className="text-[10px] text-gray-400 uppercase tracking-widest bg-gray-50 border-b border-gray-100 font-black">
                    <tr>
                        <th className="px-6 py-4">Servicio / Paquete</th>
                        <th className="px-6 py-4 text-right">Coste Interno Base</th>
                        <th className="px-6 py-4 text-right">Precio de Venta (PVP)</th>
                        <th className="px-6 py-4 text-right">Rentabilidad Bruta</th>
                        <th className="px-6 py-4 text-center">Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {productos.length === 0 ? (
                        <tr><td colSpan="5" className="text-center py-12 text-gray-400 font-bold uppercase tracking-widest">El catálogo está vacío</td></tr>
                    ) : (
                        productos.map((item) => {
                            const isEditing = editingId === item.id;
                            
                            // Lógica de compatibilidad para leer datos viejos o nuevos
                            const costoInterno = isEditing ? parseFloat(editForm.costoBase) || 0 : parseFloat(item.costoBase) || parseFloat(item.materiales) || 0;
                            const precioVenta = isEditing ? parseFloat(editForm.precioSugerido) || 0 : parseFloat(item.precioSugerido) || parseFloat(item.costo) || 0;
                            
                            // Calculamos ganancia
                            const gananciaEuros = Math.max(0, precioVenta - costoInterno);
                            const gananciaPorcentaje = precioVenta > 0 ? ((gananciaEuros / precioVenta) * 100).toFixed(1) : 0;
                            
                            // Determinamos color de la rentabilidad
                            const targetMargin = parseFloat(margenObjetivo);
                            let rentabilidadColor = 'text-kokimiko-verde';
                            if (gananciaPorcentaje < targetMargin) rentabilidadColor = 'text-kokimiko-naranja';
                            if (gananciaPorcentaje < (targetMargin / 2)) rentabilidadColor = 'text-red-500';

                            return (
                                <tr key={item.id} className="border-b border-gray-50 hover:bg-kokimiko-teal/5 transition-colors">
                                    <td className="px-6 py-4 font-black text-kokimiko-texto max-w-xs truncate">{item.producto}</td>
                                    
                                    <td className="px-6 py-4 text-right">
                                        {isEditing ? (
                                            <input type="number" step="0.01" className="w-24 p-2 bg-gray-50 border border-gray-200 rounded-xl text-right font-bold text-gray-600 focus:ring-2 focus:ring-kokimiko-teal outline-none" value={editForm.costoBase} onChange={(e) => setEditForm({...editForm, costoBase: e.target.value})} />
                                        ) : (
                                            <span className="font-bold text-gray-500">{formatCurrency(costoInterno)}</span>
                                        )}
                                    </td>
                                    
                                    <td className="px-6 py-4 text-right">
                                        {isEditing ? (
                                            <input type="number" step="0.01" className="w-24 p-2 bg-kokimiko-teal/10 border border-kokimiko-teal/30 rounded-xl text-right font-black text-kokimiko-teal focus:ring-2 focus:ring-kokimiko-teal outline-none" value={editForm.precioSugerido} onChange={(e) => setEditForm({...editForm, precioSugerido: e.target.value})} />
                                        ) : (
                                            <span className="font-black text-kokimiko-teal text-base">{formatCurrency(precioVenta)}</span>
                                        )}
                                    </td>

                                    <td className="px-6 py-4 text-right">
                                        <div className="flex flex-col items-end">
                                            <span className={`font-black ${rentabilidadColor}`}>{gananciaPorcentaje}%</span>
                                            <span className="text-[10px] font-bold text-gray-400 uppercase">+{formatCurrency(gananciaEuros)}</span>
                                        </div>
                                    </td>
                                    
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex justify-center gap-2">
                                            {isEditing ? (
                                                <>
                                                    <button onClick={() => handleSave(item.id)} className="p-2 bg-green-50 text-green-500 hover:bg-green-500 hover:text-white rounded-lg transition-colors" title="Guardar"><FaSave size={16} /></button>
                                                    <button onClick={() => setEditingId(null)} className="p-2 bg-gray-50 text-gray-400 hover:bg-gray-200 rounded-lg transition-colors" title="Cancelar"><FaTimes size={16} /></button>
                                                </>
                                            ) : (
                                                <>
                                                    <button onClick={() => handleEditClick(item)} className="p-2 bg-gray-50 text-gray-400 hover:text-kokimiko-texto hover:bg-gray-100 rounded-lg transition-colors" title="Editar"><FaEdit size={16} /></button>
                                                    <button onClick={() => eliminarProducto(item.id)} className="p-2 bg-red-50 text-kokimiko-naranja hover:bg-kokimiko-naranja hover:text-white rounded-lg transition-colors" title="Eliminar"><FaTrashAlt size={16} /></button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
}